import { alpha, Typography } from '@mui/material';
import type { FC } from 'react';

import type {
  DialPadButtonContent,
  DialPadValue,
} from 'features/dial-pad/interfaces';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';

interface DialPadButtonProps extends DialPadButtonContent {
  onMouseDown: (value: DialPadValue) => void;
}

export const DialPadButton: FC<DialPadButtonProps> = ({
  value,
  text,
  onMouseDown,
}) => {
  const isZeroPlus = value === 0 && text === '+';
  const isSpecial = text === '*' || text === '#';

  return (
    <DialButton onMouseDown={() => onMouseDown(value)}>
      <Value
        $isSpecial={isZeroPlus ? false : isSpecial}
        variant='dialPadButtonValue'
      >
        {value}
      </Value>
      {isZeroPlus ? (
        <PlusText data-testid='dialpad-plus'>{text}</PlusText>
      ) : (
        <Text>{text}</Text>
      )}
    </DialButton>
  );
};

const DialButton = styled('button')`
  width: 100%;
  height: 63px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 0;
  background-color: #2f3438;
  border: 0.5px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  outline: none;
  transition:
    transform 0.2s,
    background-color 0.2s;

  &:hover {
    background-color: ${alpha(theme.palette.grey[800], 0.9)};
  }

  &:active {
    transform: scale(0.98);
    background-color: ${alpha(theme.palette.grey[700], 0.9)};
  }
`;

const Value = styled(Typography, {
  // Prevent $isSpecial prop from being forwarded to DOM to avoid React warning
  shouldForwardProp: (prop) => prop !== '$isSpecial',
})<{ $isSpecial?: boolean }>`
  color: #fff;
  font-size: ${({ $isSpecial }) => ($isSpecial ? '22px' : '20px')};
  font-weight: ${({ $isSpecial }) => ($isSpecial ? 600 : 500)};
  line-height: 1;
  margin-bottom: ${theme.spacing(0.25)};
`;

const Text = styled(Typography)`
  color: ${alpha(theme.palette.common.white, 0.7)};
  font-size: 10px;
  font-weight: 400;
  line-height: 1;
  text-align: center;
  letter-spacing: 0.5px;
`;

const PlusText = styled(Typography)`
  color: ${alpha(theme.palette.common.white, 0.7)};
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  text-align: center;
  letter-spacing: 0.5px;
  margin-top: 2px;
`;
